import { CoreUtils } from '../core';
import { gatherAllChildrenNodes } from '../gather-all-children-nodes';
import Big from 'big.js';
import accounting from 'accounting';
import { FORMATS } from '../core/formats';
import moment from 'moment-timezone';
import cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _isObject from 'lodash/isObject';
import _groupBy from 'lodash/groupBy';
import countries from 'i18n-iso-countries';
countries.registerLocale(require('i18n-iso-countries/langs/en.json'));
export const RestaurantUtils = {
    store: {
        url: (opts) => {
            if (opts.isTest) {
                return 'http://localhost:3000';
            }
            return opts.domain ? `https://${opts.domain}` : `https://${opts.subdomain}.${opts.storeHost}`;
        },
    },
    opening_hours: {
        getOpenDays(hours) {
            return hours.map(({ day }) => day);
        },
        isTimeWithin({ dt, hours, special_hours, first_offset, last_offset, timezone, isLogRequire = false }) {
            isLogRequire && console.log("[isTimeWithin DEBUG] input: ", "Datetime: ", dt.format(), "timezone: ", timezone);
            let matchIndex = -1;
            let isWithin = false;
            const weekDate = dt.format(FORMATS.moment.date);
            const weekday = dt.clone().locale('en').format('dddd');
            const filtered_special_hours = (special_hours || [])
                .map((h, i) => ({ ...h, index: i }))
                .filter(h => h.date === weekDate);
            const isSpecialHours = filtered_special_hours.length > 0;
            const opening_hours = isSpecialHours
                ? filtered_special_hours
                : hours.map((h, i) => ({ ...h, index: i })).filter(h => h.day === weekday);
            for (const slot of opening_hours) {
                // @ts-ignore
                if (isSpecialHours && slot.opened === false) {
                    continue;
                }
                if (slot.h24) {
                    isWithin = true;
                    matchIndex = slot.index;
                    break;
                }
                const date = dt.locale('en').format(FORMATS.moment.date);
                const open = moment.tz(`${date} ${slot.open}`, FORMATS.moment.datetime, timezone).add(first_offset, 'minutes');
                const close = moment
                    .tz(`${date} ${slot.close}`, FORMATS.moment.datetime, timezone)
                    .subtract(last_offset, 'minutes');
                isLogRequire && console.log("[isTimeWithin DEBUG] open: ", open.format(), " close: ", close.format(), " dt: ", dt.format());
                isLogRequire && console.log("[isTimeWithin DEBUG] store is opening: ", dt.isAfter(open) && dt.isBefore(close));
                if (dt.isSameOrAfter(open) && dt.isSameOrBefore(close)) {
                    // IF CURRENT BETWEEN OPEN AND CLOSE
                    isWithin = true;
                    matchIndex = slot.index;
                    break;
                }
            }
            return { isWithin, isSpecialHours, index: matchIndex };
        },
        closesIn({ dt, hours, special_hours, first_offset, last_offset, timezone, }) {
            const { isWithin, index, isSpecialHours } = this.isTimeWithin({
                dt,
                hours,
                special_hours,
                first_offset,
                last_offset,
                timezone,
            });
            if (!isWithin)
                return null;
            const slot = isSpecialHours ? special_hours[index] : hours[index];
            let continuesToNextDay = false;
            let nextSlot = null;
            if (slot.close === '23:59' || slot.h24) {
                const nextWeekday = CoreUtils.dates.getNextWeekday(slot.day);
                const nextDayTimes = hours.filter(s => s.day === nextWeekday);
                for (const s of nextDayTimes) {
                    if (s.h24 || s.open === '00:00') {
                        continuesToNextDay = true;
                        nextSlot = s;
                        break;
                    }
                }
            }
            const parsed = moment
                .tz(`${dt.format(FORMATS.moment.date)} ${slot.close}`, FORMATS.moment.datetime, timezone)
                .subtract(last_offset, 'minutes');
            if (continuesToNextDay && nextSlot) {
                parsed.add('day', 1);
                parsed.set('hour', parseInt(nextSlot.close.split(':')[0], 10));
                parsed.set('minute', parseInt(nextSlot.close.split(':')[1], 10));
                parsed.subtract(last_offset, 'minutes');
            }
            const closesInMinutes = Math.round(parsed.diff(dt, 'minutes'));
            const closesInMillis = Math.round(parsed.diff(dt, 'milliseconds'));
            return closesInMinutes >= 0 && closesInMinutes <= 30
                ? {
                    minutes: closesInMinutes,
                    millis: closesInMillis,
                }
                : null;
        },
    },
    theme: {
        fromRestaurant(r) {
            const { fonts, colors, sections } = r.website;
            return {
                font: {
                    header: `'${fonts.heading.family}', ${fonts.heading.category};`,
                    body: `'${fonts.body.family}',Verdana,Helvetica,${fonts.body.category};`,
                    size: 16,
                },
                colors: {
                    primary: colors.primary,
                    primary_text: colors.primary_text,
                    text: colors.text,
                    background: colors.background,
                    success: '#51a351',
                    warning: '#f89406',
                    error: '#bd362f',
                },
                color: {
                    primary: colors.primary,
                    primary_text: colors.primary_text,
                    text: colors.text,
                    background: colors.background,
                    success: '#51a351',
                    warning: '#f89406',
                    error: '#bd362f',
                },
                status_colors: {
                    complete: '#51A351',
                    onroute: '#00BFFF',
                    ready: '#0080FF',
                    confirmed: '#B7D024',
                    unconfirmed: '#efbb1e',
                    awaiting_payment: '#efbb1e',
                    cancelled: '#BD362F',
                },
                button: {
                    height: 38,
                },
                box: {
                    background: colors.box,
                    text: colors.box_text,
                    border: CoreUtils.colors.shadeAlternate(0.16, colors.box),
                },
                input: {
                    background: CoreUtils.colors.shadeAlternate(0.02, colors.box),
                    text: CoreUtils.colors.shadeAlternate(0.02, colors.box_text),
                    border: CoreUtils.colors.shadeAlternate(0.2, colors.box),
                    height: 38,
                    fontSize: 16,
                },
                top_nav: {
                    zindex: 10,
                    height: 70,
                    background_color: sections.top_nav.colors.background,
                    text_color: sections.top_nav.colors.text,
                },
                side_nav: {
                    zindex: 9,
                    width: 70,
                    background_color: colors.box,
                    text_color: colors.box_text,
                },
                slide_panel: {
                    zindex: 9,
                    background_color: colors.box,
                    text_color: colors.box_text,
                },
                footer: {
                    background: sections.footer.colors.background,
                    text: sections.footer.colors.text,
                },
                content_width: {
                    xs: 360,
                    sm: 480,
                    md: 720,
                    lg: 1140,
                    xl: 1360,
                },
                breakpoints: CoreUtils.ui.breakpoints,
            };
        },
    },
    menu: {
        getCategories(r) {
            const categories = [];
            for (const menu of r.menus) {
                for (const category of menu.categories) {
                    categories.push(category);
                }
            }
            return categories;
        },
        getDishes(r) {
            const dishes = [];
            for (const menu of r.menus) {
                for (const category of menu.categories) {
                    for (const dish of category.dishes) {
                        dishes.push(dish);
                    }
                }
            }
            return dishes;
        },
        findCategoryById(r, category_id) {
            for (let menuIndex = 0; menuIndex < r.menus.length; menuIndex++) {
                const menu = r.menus[menuIndex];
                for (let categoryIndex = 0; categoryIndex < menu.categories.length; categoryIndex++) {
                    const category = menu.categories[categoryIndex];
                    if (category._id === category_id) {
                        return { menu, category, menuIndex, categoryIndex };
                    }
                }
            }
            return null;
        },
        findDish(r, dish_id) {
            for (const menu of r.menus) {
                for (const category of menu.categories) {
                    for (const dish of category.dishes) {
                        if (dish._id === dish_id) {
                            return { dish, category, menu };
                        }
                    }
                }
            }
            return null;
        },
        findDishCategory(r, dish_id) {
            for (const menu of r.menus) {
                for (const category of menu.categories) {
                    for (const dish of category.dishes) {
                        if (dish._id === dish_id) {
                            return category;
                        }
                    }
                }
            }
            return null;
        },
        filterDishes(r, filter) {
            const dishes = [];
            for (const menu of r.menus) {
                for (const category of menu.categories) {
                    for (const dish of category.dishes) {
                        if (filter(dish)) {
                            dishes.push(dish);
                        }
                    }
                }
            }
            return dishes;
        },
        getTree(r) {
            return r.menus
                .filter(m => m.categories.length)
                .map(menu => ({
                value: menu._id,
                label: menu.name,
                children: menu.categories
                    .filter(c => c.dishes.length)
                    .map(category => ({
                    value: category._id,
                    label: category.name,
                    children: category.dishes.map(dish => ({
                        value: dish._id,
                        label: dish.name,
                    })),
                })),
            }))
                .filter(node => node.children.length);
        },
        getTreeFilter(r, filter) {
            return r.menus
                .filter(m => m.categories.length)
                .map(menu => ({
                value: menu._id,
                label: menu.name,
                type: 'menu',
                children: menu.categories
                    .filter(c => c.dishes.length)
                    .map(category => ({
                    value: category._id,
                    label: category.name,
                    type: 'category',
                    children: category.dishes.filter(filter).map(dish => ({
                        value: dish._id,
                        label: dish.name,
                        type: 'dish',
                    })),
                })),
            }))
                .filter(node => node.children.length);
        },
        getAllDishIds(r) {
            const DishTreeData = this.getTree(r);
            return gatherAllChildrenNodes(DishTreeData);
        },
        itemAssignedToAnyReceiptPrinter(printers, itemId) {
            if (!itemId || !printers?.length)
                return false;
            return printers.some((p) => (p?.receipt?.eligible_items || []).includes(itemId));
        },
        receiptPrinterIdsAssigningItem(printers, itemId) {
            if (!itemId || !printers?.length)
                return [];
            return printers
                .filter((p) => p._id && (p?.receipt?.eligible_items || []).includes(itemId))
                .map((p) => p._id);
        },
        assignItemsToPrinterV2(r, itemIds, printerIds) {
            const printers = r?.settings?.printers || [];
            const updatedPrinters = printers.map(printer => {
                let eligible_items = new Set(printer.receipt.eligible_items || []);
                if (printerIds.includes(printer._id)) {
                    eligible_items = new Set([...eligible_items, ...itemIds]);
                }
                else {
                    eligible_items = new Set([...eligible_items].filter(id => !itemIds.includes(id)));
                }
                return {
                    ...printer,
                    receipt: {
                        ...printer.receipt,
                        eligible_items: Array.from(eligible_items),
                    }
                };
            });
            return {
                updatedPrinters,
                updateQuery: { $set: { 'settings.printers': updatedPrinters } }
            };
        },
        getAssignPrintersByTypeV2(r, type, id) {
            const printers = r?.settings?.printers || [];
            const assignedPrinters = [];
            const allItems = r.menus_v2?.map(m => m.items).flat() || [];
            let item_ids = [];
            if (type === 'menu') {
                item_ids = allItems.filter((i) => i.menu_id === id).map((i) => i.id);
            }
            else if (type === 'category') {
                item_ids = allItems.filter((i) => i.category_id === id).map((i) => i.id);
            }
            for (const printer of printers) {
                const eligible_items = new Set(printer.receipt.eligible_items || []);
                if (type === 'dish' && eligible_items.has(id)) {
                    assignedPrinters.push(printer._id);
                    continue;
                }
                if (item_ids.length && item_ids.every((item_id) => eligible_items.has(item_id))) {
                    assignedPrinters.push(printer._id);
                }
            }
            return assignedPrinters;
        }
    },
    dish: {
        calculateTotal(dish) {
            let price;
            if (dish.price === null) {
                price = Big(0);
            }
            else {
                price = Big(dish.price);
            }
            let base_price = price;
            let modified = Big(0);
            if (dish.type === 'combo') {
                // CALCULATE MODIFIER TOTALS AND DIFFERENCE PRICES FOR EACH CHOICE
                const choice_difference_totals = [];
                const option_set_totals = [];
                for (const choice of dish.choices) {
                    if (choice.selected) {
                        const choiceDish = choice.selected;
                        if (choiceDish.option_sets && choiceDish.option_sets.length > 0) {
                            const { total } = RestaurantUtils.dish.optionSetTotal(choiceDish.option_sets);
                            option_set_totals.push(total);
                        }
                        if (dish.price_type === 'difference') {
                            const lowest_price_option = choice.lpo;
                            if (Big(choiceDish.price).gt(lowest_price_option)) {
                                choice_difference_totals.push(Big(choiceDish.price).minus(lowest_price_option).toString());
                            }
                        }
                    }
                }
                const option_set_total = option_set_totals.reduce((total, modifier) => Big(total).plus(modifier), Big(0));
                const choice_difference_total = choice_difference_totals.reduce((total, modifier) => Big(total).plus(modifier), Big(0));
                modified = modified.plus(option_set_total).plus(choice_difference_total);
                price = price.plus(option_set_total);
                price = price.plus(choice_difference_total);
            }
            else {
                const { total } = RestaurantUtils.dish.optionSetTotal(dish.option_sets);
                modified = modified.plus(total);
                price = price.plus(total);
            }
            price = price.times(dish.qty);
            return {
                base_price: base_price.toFixed(),
                price: price.toFixed(),
                modified: modified.toFixed(),
            };
        },
        getSelectedVariableOption(optionSet, optionSetList) {
            const variablePriceOptionSet = optionSet.variable_price_ref
                ? optionSetList.find(set => set._id === optionSet.variable_price_ref) || null
                : null;
            let selectedVariableOption = '';
            if (variablePriceOptionSet) {
                for (let i = 0; i < variablePriceOptionSet.options.length; i++) {
                    if (variablePriceOptionSet.options[i].quantity > 0) {
                        selectedVariableOption = variablePriceOptionSet.options[i]._id;
                        break;
                    }
                }
            }
            return selectedVariableOption;
        },
        getOptionSetOptionPricingData(optionSets, optionSet, option) {
            let pricingData;
            const selectedVariableOption = RestaurantUtils.dish.getSelectedVariableOption(optionSet, optionSets);
            const dependedOptionSet = optionSets.find(os => os._id === optionSet.variable_price_ref);
            const isDependedOptionSetRequired = dependedOptionSet && dependedOptionSet.conditions.required;
            if (selectedVariableOption) {
                pricingData = option.prices[selectedVariableOption];
            }
            else {
                if (isDependedOptionSetRequired)
                    return 0;
                return option.price ? option.price : 0;
            }
            return pricingData;
        },
        calculateOptionSetOptionPrice(option, optionSet, optionSetList) {
            let pricingData = RestaurantUtils.dish.getOptionSetOptionPricingData(optionSetList, optionSet, option);
            // If we don't use the point system for the option set,
            // use the old price calculation for backward compatibility.
            if (!optionSet.using_points) {
                return pricingData;
            }
            // Excluding the option, if it's free based on points.
            const freeOptions = optionSet.freeOptions || [];
            if (freeOptions.includes(option._id)) {
                return 0;
            }
            const position = option.position;
            const multiplier = option.quantity_multiplier || option.quantity_multiplier_price;
            // Calculate the pricing data when using the point system.
            // This will be an object containing single, double pricing values
            // with respect to half (left and right) and whole positions.
            pricingData = pricingData;
            if (!_isObject(pricingData)) {
                return 0;
            }
            // Return the correct pricing value based on position and quantity multiplier.
            // A lot of if-else statements here, but just to keep conditions clear.
            if (position === 'whole') {
                if (multiplier === 'single') {
                    return pricingData.whole.single;
                }
                else if (multiplier === 'double') {
                    return pricingData.whole.double;
                }
            }
            else if (position === 'left' || position === 'right') {
                if (multiplier === 'single') {
                    return pricingData.half.single;
                }
                else if (multiplier === 'double') {
                    return pricingData.half.double;
                }
            }
            return 0;
        },
        optionSetTotal(optionSets) {
            let total = Big(0);
            const freeTillIndexs = [];
            for (let i = 0; i < optionSets.length; i++) {
                const optionSet = optionSets[i];
                const { conditions } = optionSet;
                const freeQty = conditions.free_amount;
                let freeUsed = 0;
                for (let index = 0; index < optionSet.options.length; index++) {
                    const option = optionSet.options[index];
                    if (option.quantity > 0) {
                        for (let y = 0; y < option.quantity; y++) {
                            if (conditions.multi_select && freeQty && freeUsed < freeQty) {
                                freeUsed++;
                            }
                            else {
                                if (freeTillIndexs[i] === undefined && freeQty) {
                                    freeTillIndexs[i] = index;
                                }
                                const price = RestaurantUtils.dish.calculateOptionSetOptionPrice(option, optionSet, optionSets);
                                total = Big(total).plus(price || 0);
                            }
                        }
                    }
                }
            }
            return {
                total: total.toString(),
                freeTillIndexs: freeTillIndexs,
            };
        },
        optionSetValidate(option_sets) {
            const errors = [];
            for (const set of option_sets) {
                let ok = false;
                const conditions = set.conditions;
                const total = set.options.reduce((t, o) => t + o.quantity, 0);
                if (conditions.required) {
                    if (conditions.multi_select) {
                        if (conditions.max_options) {
                            if ((conditions.min_options || 1) <= total && total <= conditions.max_options) {
                                ok = true;
                            }
                        }
                        else if (total >= (conditions.min_options || 1)) {
                            ok = true;
                        }
                    }
                    else if (total === 1) {
                        ok = true;
                    }
                }
                // tslint:disable-next-line
                else {
                    if (conditions.max_options) {
                        if (total <= conditions.max_options) {
                            ok = true;
                        }
                    }
                    else {
                        ok = true;
                    }
                }
                errors.push(!ok);
            }
            return errors;
        },
        getOptionSetOptionDisplayingQuantityMultiplier(option) {
            switch (option.quantity_multiplier) {
                case 'double':
                    return 'x2';
                default:
                    return '';
            }
        },
        groupOptionSetOptionByPosition(optionSet) {
            const positionOrder = ['whole', 'left', 'right'];
            const options = _groupBy(optionSet.options.filter(option => option.quantity > 0), 'position');
            return Object.keys(options)
                .sort((a, b) => {
                return positionOrder.indexOf(a) - positionOrder.indexOf(b);
            })
                .reduce((obj, key) => {
                obj[key] = options[key];
                return obj;
            }, {});
        },
    },
    cart: {},
    trim: {
        option_sets(option_sets) {
            if (option_sets && option_sets.length > 0) {
                for (const [k, os] of option_sets.entries()) {
                    // @ts-ignore
                    delete option_sets[k].show_in_menu;
                    let option_length = os.options.length;
                    while (option_length--) {
                        const optionQty = os.options[option_length].quantity;
                        if (!Number.isInteger(optionQty) || optionQty === 0) {
                            option_sets[k].options.splice(option_length, 1);
                        }
                    }
                }
            }
        },
        dish(dish) {
            // @ts-ignore
            delete dish.image;
            // @ts-ignore
            delete dish.description;
            delete dish.cart_item_id;
            if (dish.type === 'combo') {
                for (let i = 0; i < dish.choices.length; i++) {
                    this.option_sets(dish.choices[i].selected.option_sets);
                    // @ts-ignore
                    delete dish.choices[i].dishes;
                }
            }
            else {
                this.option_sets(dish.option_sets);
            }
            return dish;
        },
    },
    untrim: {
        option_sets(dish, r) {
            if (dish.option_sets && dish.option_sets.length > 0) {
                const sets = dish.option_sets.map(os => {
                    const baseSet = r.option_sets.find(set => set._id === os._id);
                    if (!baseSet)
                        return os;
                    return {
                        ...cloneDeep(baseSet),
                        options: os.options.map(o => {
                            const baseOption = baseSet.options.find(opt => opt._id === o._id);
                            if (!baseOption)
                                return o;
                            return {
                                ...cloneDeep(baseOption),
                                quantity: o.quantity
                            };
                        })
                    };
                });
                dish.option_sets = sets;
            }
            return dish;
        },
        dish(dish, r) {
            const current = RestaurantUtils.menu.findDish(r, dish._id);
            if (current) {
                dish.image = current.dish.image;
                dish.description = current.dish.description;
                if (dish.type === 'combo') {
                    for (let i = 0; i < dish.choices.length; i++) {
                        if (dish.choices[i].selected) {
                            this.option_sets(dish.choices[i].selected, r);
                            const currentChoice = current.dish.choices.find(c => c._id === dish.choices[i]._id);
                            dish.choices[i].dishes = cloneDeep(currentChoice.dishes);
                        }
                    }
                }
                else {
                    this.option_sets(dish, r);
                }
            }
            return dish;
        },
    },
    order: {
        color(status) {
            switch (status) {
                case 'unconfirmed':
                    return '#efbb1e';
                case 'confirmed':
                    return '#B7D024';
                case 'ready':
                    return '#0080FF';
                case 'on_route':
                    return '#00BFFF';
                case 'complete':
                case 'seated':
                    return '#51A351';
                case 'cancelled':
                    return '#BD362F';
                default:
                    return '#efbb1e';
            }
        },
        payment: {
            poli: {
                statusFromTransaction: (TransactionStatusCode) => {
                    return TransactionStatusCode === 'Completed'
                        ? 'success'
                        : TransactionStatusCode === 'Cancelled'
                            ? 'error'
                            : TransactionStatusCode === 'Failed'
                                ? 'error'
                                : TransactionStatusCode === 'ReceiptUnverified'
                                    ? 'error'
                                    : TransactionStatusCode === 'TimedOut'
                                        ? 'error'
                                        : 'pending';
                },
            },
            ipay88: {
                totalToAmount(total) {
                    return accounting.formatMoney(total, '', 2, ',', '.');
                },
            },
        },
    },
    settings: {
        calculateEstimatedWaitTime(service, s) {
            if (service === 'delivery') {
                if (s.status.times.confirmed !== '' && s.status.times.ready !== '' && s.status.times.on_route !== '') {
                    return (s.status.times.confirmed || 0) + (s.status.times.ready || 0) + (s.status.times.on_route || 0);
                }
                return null;
            }
            if (s.status.times.confirmed !== '' && s.status.times.ready !== '') {
                return (s.status.times.confirmed || 0) + (s.status.times.ready || 0);
            }
            return null;
        },
        defaultGuestMatchKeys: () => ['details.name', 'details.phone', 'details.email'],
        defaultAudioNotifications() {
            return {
                sound: 'job-done.ogg',
                repeat_count: 1,
                stop_on_click: false,
                play_on: {
                    order_new: true,
                    booking_new: true,
                    customer_age_verification: true,
                },
            };
        },
        dateTimeFormat(region) {
            return `${region.formats.date} ${region.formats.time}`;
        },
        paymentActiveCash(payments) {
            return !!(payments.cash && payments.cash.enabled);
        },
        paymentActiveCard(payments) {
            return !!(payments.card && payments.card.enabled);
        },
        paymentActiveStripe(payments) {
            const method = payments.stripe;
            return !!(method && method.enabled && method.secret_key && method.publishable_key && method.currency);
        },
        paymentActivePaypal(payments) {
            const method = payments.paypal;
            return !!(method && method.enabled && method.client_id && method.secret && method.currency);
        },
        paymentActiveCardConnect(payments) {
            const method = payments.cardconnect;
            return !!(method &&
                method.enabled &&
                method.site &&
                method.username &&
                method.password &&
                method.merchid &&
                method.currency);
        },
        paymentActivePoliPay(payments) {
            const method = payments.poli_pay;
            return !!(method && method.enabled && method.merchant_id && method.auth_code && method.currency);
        },
        paymentActivePaygatePayweb(payments) {
            const method = payments.paygate_payweb;
            return !!(method && method.enabled && method.paygate_id && method.secret && method.currency);
        },
        paymentActiveIpay88(payments) {
            const method = payments.ipay88;
            return !!(method &&
                method.enabled &&
                method.api_entry_url &&
                method.api_query_url &&
                method.merchant_key &&
                method.merchant_code &&
                method.currency);
        },
        paymentActiveBamboraAPAC(payments) {
            const method = payments.bambora_apac;
            return !!(method &&
                method.enabled &&
                method.merchant_id &&
                method.api_username &&
                method.api_password &&
                method.currency);
        },
        paymentActiveBamboraNA(payments) {
            const method = payments.bambora_na;
            return !!(method && method.enabled && method.merchant_id && method.passcode);
        },
        paymentActivePesapal(payments) {
            const method = payments.pesapal;
            return !!(method && method.enabled && method.consumer_secret && method.consumer_key && method.currency);
        },
        paymentActiveATHMovil(payments) {
            const method = payments.ath_movil;
            return !!(method &&
                method.enabled &&
                method.public_token &&
                method.private_token &&
                method.env &&
                method.lang_code &&
                // method.theme &&
                method.currency);
        },
        paymentActiveCheckout(payments) {
            const method = payments.checkout;
            return !!((method && method.enabled)
            // method.secret_key &&
            // method.publishable_key &&
            // method.currency
            );
        },
        paymentActiveCheckoutApplePay(payments) {
            const method = payments.checkout_apple_pay;
            return !!(method &&
                method.enabled &&
                method.checkout_public_key &&
                method.checkout_secret_key &&
                method.apple_pay_merchant_name &&
                method.apple_pay_merchant_id &&
                method.apple_pay_merchant_domain &&
                method.apple_pay_merchant_identity_cert &&
                method.apple_pay_merchant_identity_key);
        },
        paymentActiveCheckoutGooglePay(payments) {
            const method = payments.checkout_google_pay;
            return !!(method &&
                method.enabled &&
                method.checkout_public_key &&
                method.checkout_secret_key &&
                method.google_pay_merchant_id &&
                method.google_pay_merchant_name);
        },
        paymentActiveStripeDigitalWallet(payments) {
            const method = payments.stripe_digital_wallet;
            return !!(method && method.enabled && method.stripe_secret_key && method.stripe_public_key);
        },
        paymentActiveRazorPay(payments) {
            const method = payments.razor_pay;
            return !!(method && method.enabled && method.razor_pay_secret_key && method.razor_pay_public_key);
        },
        paymentActiveGravity(payments) {
            const method = payments.gravity;
            return !!(method && method.enabled);
        },
        paymentActiveAuthorized(payments) {
            const method = payments.authorized;
            return !!(method && method.enabled);
        },
        paymentActiveElavon(payments) {
            const method = payments.elavon;
            return !!(method && method.enabled && method.ssl_merchant_id && method.ssl_user_id && method.ssl_pin);
        },
        paymentActiveRedDot(payments) {
            const method = payments.red_dot;
            return !!(method && method.enabled && method.merchant_id && method.secret_key);
        },
        paymentActiveGkash(payments) {
            const method = payments.gkash;
            return !!(method && method.enabled && method.merchant_id && method.signature_key);
        },
        paymentActiveSGE(payments) {
            const method = payments.sgepay;
            return !!(method && method.enabled && method.Mid && method.RequestId);
        },
        paymentActivePayWay(payments) {
            const method = payments.payway;
            return !!(method && method.enabled);
        },
        paymentActiveStripeBancontact(payments) {
            const method = payments.bancontact;
            return !!(method && method.enabled && method.currency);
        },
        paymentActiveSwish(payments) {
            const method = payments.swish;
            return !!(method && method.enabled && method.env && method.number && method.cert && method.key);
        },
        paymentActiveFiserv(payments) {
            const method = payments.fiserv;
            return !!(method &&
                method.enabled &&
                method.env &&
                method.api_key &&
                method.api_secret &&
                method.store_name &&
                method.connect_url &&
                method.connect_secret);
        },
        paymentActiveVivaWallet(payments) {
            const method = payments.viva_wallet;
            return !!(method &&
                method.enabled &&
                method.env &&
                method.merchant_id &&
                method.api_key &&
                method.oauth_client_id &&
                method.oauth_client_secret);
        },
        paymentActiveCabbagePay(payments) {
            const method = payments.cabbagepay;
            return !!(method &&
                method.enabled &&
                method.env &&
                method.client_id &&
                method.client_secret &&
                method.connected_account);
        },
        paymentActivePayMongo(payments) {
            const method = payments.paymongo;
            return !!(method && method.enabled && method.public_key && method.secret_key);
        },
        paymentActiveNuvei(payments) {
            const method = payments.nuvei;
            return !!(method &&
                method.enabled &&
                method.api_key &&
                method.api_secret &&
                method.user_name &&
                method.password &&
                method.public_key);
        },
        integrationFacebookPixelActive(r) {
            const i = r.settings.integrations;
            return !!(i && i.facebook_pixel && i.facebook_pixel.pixel_id);
        },
        integrationGoogleAnalyticsActive(r) {
            const i = r.settings.integrations;
            return !!(i && i.google_analytics && i.google_analytics.property_id);
        },
        integrationTookanActive(r) {
            const p = r.settings.services.delivery.providers;
            return !!(p && p.tookan && p.tookan.api_key && p.tookan.utc_offset);
        },
        integrationQuestTagActive(r) {
            const p = r.settings.services.delivery.providers;
            return !!(p && p.questtag && p.questtag.api_key && p.questtag.enabled);
        },
        integrationDriveYelloActive(r) {
            const p = r.settings.services.delivery.providers;
            return !!(p && p.yello && p.yello.secret_key);
        },
        integrationPostmatesActive(r) {
            const p = r.settings.services.delivery.providers;
            return !!(p &&
                p.postmates &&
                p.postmates.authentication_key &&
                p.postmates.customer_id &&
                p.postmates.webhook_signature_secret &&
                p.postmates.pickup_phone_number &&
                p.postmates.enabled);
        },
        integrationLalamoveActive(r) {
            const p = r.settings.services.delivery.providers;
            return !!(p &&
                p.lalamove &&
                p.lalamove.api_key &&
                p.lalamove.market &&
                p.lalamove.service_types &&
                p.lalamove.enabled);
        },
        integrationWalletlyActive(r) {
            const providers = r.settings.loyalty_providers;
            return !!(providers &&
                providers.walletly &&
                providers.walletly.enabled &&
                providers.walletly.api_key &&
                providers.walletly.brand_id &&
                providers.walletly.secret_key &&
                providers.walletly.integration_id);
        },
        integrationUberActive(r) {
            const p = r.settings.services.delivery.providers;
            return !!(p &&
                p.uber &&
                p.uber.abn &&
                p.uber.company_name &&
                p.uber.director_name &&
                p.uber.trading_name &&
                p.uber.mobile_number &&
                p.uber.enabled);
        },
        getDefaultDeliveryProvider(r) {
            let provider = r.settings.services.delivery.default_delivery_provider;
            if (!provider) {
                provider = 'restaurant';
                if (r.settings.services.delivery.providers.postmates &&
                    r.settings.services.delivery.providers.postmates.enabled)
                    provider = 'postmates';
                else if (r.settings.services.delivery.providers.questtag &&
                    r.settings.services.delivery.providers.questtag.enabled)
                    provider = 'questtag';
                else if (r.settings.services.delivery.providers.tookan && r.settings.services.delivery.providers.tookan.api_key)
                    provider = 'tookan';
                else if (r.settings.services.delivery.providers.lalamove &&
                    r.settings.services.delivery.providers.lalamove.api_key)
                    provider = 'lalamove';
                else if (r.settings.services.delivery.providers.uber && r.settings.services.delivery.providers.uber.api_key)
                    provider = 'uber';
            }
            return provider;
        },
        getDeliveryProviderName(id) {
            const names = {
                questtag: 'Shipday',
                tookan: 'Tookan',
                postmates: 'Postmates',
                getswift: 'Getswift',
                restaurant: 'Restaurant',
                lalamove: 'Lalamove',
                uber: 'Uber',
            };
            return id in names ? names[id] : id;
        },
        getCountryCodeFromLocation(r) {
            let countryName = _get(r.location.map_data, 'components.country');
            let countryCode = countries.getAlpha2Code(countryName, 'en');
            // country list checked only against stripe's supported countries
            const countryCodeWithLocalNames = {
                US: 'United States',
                JP: '日本',
                ES: 'España',
                GR: 'Ελλάδα',
                HK: '香港',
                PL: 'Polska',
                SE: 'Sverige',
                SK: 'Slovensko',
                TH: 'ประเทศไทย',
                DK: 'Danmark',
            };
            if (!countryCode) {
                countryCode =
                    Object.keys(countryCodeWithLocalNames).find(key => countryCodeWithLocalNames[key] === countryName) || '';
            }
            return countryCode;
        },
    },
    services: {
        defaultCustomerInfo: () => ['name', 'phone', 'email'],
    },
    /** Reservations v2 (calendar, /reserve, embed widget) — not legacy table_booking-only. */
    reservationsV2Enabled(r) {
        return (r.settings?.services?.table_booking?.enabled === true &&
            r.reservations_integration?.enabled === true);
    },
    /**
     * Storefront POST /booking/slot-availability — Reservations v2 only.
     * Legacy (v1) venues match prod release-1.0.0: no table-layout slot gating on guest create,
     * even when dine-in tables exist (QR dine-in tables are not booking inventory there).
     */
    guestBookingSlotAvailabilityEnabled(r) {
        return RestaurantUtils.reservationsV2Enabled(r);
    },
};
//# sourceMappingURL=index.js.map